import type { ItineraryDay } from '../../../types';
import type { ItineraryRouteDay } from '../../../api/directions.api';
import { decodePolylinePoints, type LatLngPoint } from '../../../utils/polyline';

/**
 * The trip's route reduced to something a canvas can draw.
 *
 * The sticker's map is not decoration: it's the silhouette the traveller
 * actually drew across the map, every day of the trip at once. Real road
 * geometry comes from `POST /trips/:id/directions/route` (server-cached 30d, the
 * very same call the map tab makes). When that isn't available we still draw a
 * shape — a smooth curve through the stops — rather than nothing.
 */

const EARTH_RADIUS_M = 6_371_000;

export interface RouteStop {
  activityId: string;
  label: string;
  point: LatLngPoint;
  /** Index into the trip's days — drives the per-day colouring of the path. */
  dayIdx: number;
}

export interface TripRouteShape {
  /** One entry per drawable leg, in geographic space. */
  paths: { dayIdx: number; points: LatLngPoint[] }[];
  stops: RouteStop[];
  distanceM: number;
  /** True when the path is interpolated between stops, not real road geometry. */
  approximate: boolean;
}

export interface Point { x: number; y: number }

export interface FittedRouteShape {
  paths: { dayIdx: number; points: Point[] }[];
  stops: (RouteStop & Point)[];
}

export interface Box { x: number; y: number; w: number; h: number }

function haversineMeters(a: LatLngPoint, b: LatLngPoint): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(s)));
}

function stopLabel(title: string, venueName: string | null): string {
  const label = (venueName ?? '').trim() || title.trim();
  return label;
}

/**
 * Every stop of the trip with coordinates, in itinerary order. Origin cards are
 * skipped: they repeat the previous day's last stop so the day knows where it
 * starts, and repeating that name on the sticker just reads as a duplicate.
 */
export function collectStops(days: ItineraryDay[]): RouteStop[] {
  const stops: RouteStop[] = [];
  days.forEach((day, dayIdx) => {
    const activities = [...day.activities].sort((a, b) => a.sortOrder - b.sortOrder);
    for (const activity of activities) {
      if (activity.isOrigin) continue;
      if (activity.latitude == null || activity.longitude == null) continue;
      stops.push({
        activityId: activity.id,
        label: stopLabel(activity.title, activity.venueName),
        // The API serialises coordinates as strings ("64.00273") whatever the
        // type says; the Catmull-Rom blend's `-a + c` then concatenates and
        // every point of the curve comes out NaN.
        point: { lat: Number(activity.latitude), lng: Number(activity.longitude) },
        dayIdx,
      });
    }
  });
  return stops;
}

/**
 * Catmull-Rom through the stops, so the fallback path curves like a drawn route
 * instead of showing the trip as a polygon of straight hops.
 */
function smoothThrough(points: LatLngPoint[], samplesPerSegment = 12): LatLngPoint[] {
  if (points.length < 3) return points;

  const out: LatLngPoint[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i === 0 ? 0 : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2 < points.length ? i + 2 : points.length - 1];

    for (let s = 0; s < samplesPerSegment; s++) {
      const t = s / samplesPerSegment;
      const t2 = t * t;
      const t3 = t2 * t;
      const blend = (a: number, b: number, c: number, d: number) =>
        0.5 * ((2 * b) + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
      out.push({
        lat: blend(p0.lat, p1.lat, p2.lat, p3.lat),
        lng: blend(p0.lng, p1.lng, p2.lng, p3.lng),
      });
    }
  }
  out.push(points[points.length - 1]);
  return out;
}

/** Real road geometry, day by day. Null when there's nothing routable. */
function shapeFromRouteDays(
  days: ItineraryDay[],
  routeDays: ItineraryRouteDay[],
  stops: RouteStop[],
): TripRouteShape | null {
  const dayIdxById = new Map(days.map((d, i) => [d.id, i]));
  const paths: TripRouteShape['paths'] = [];
  let distanceM = 0;

  for (const routeDay of routeDays) {
    const dayIdx = dayIdxById.get(routeDay.dayId);
    if (dayIdx == null) continue;

    for (const leg of routeDay.legs) {
      if (!leg) continue;
      distanceM += leg.distanceM;
      // A leg that crosses water arrives split into segments; drawing those
      // keeps the ferry hop on the map instead of a straight line over the sea.
      const encoded = leg.segments.length > 0
        ? leg.segments.map(s => s.polyline)
        : [leg.polyline];
      for (const polyline of encoded) {
        const points = decodePolylinePoints(polyline);
        if (points.length >= 2) paths.push({ dayIdx, points });
      }
    }
  }

  if (paths.length === 0) return null;
  return { paths, stops, distanceM, approximate: false };
}

/** Nothing to route (or the call failed): join the stops with a smooth curve. */
function shapeFromStops(stops: RouteStop[]): TripRouteShape {
  const distanceM = stops.reduce(
    (sum, stop, i) => (i === 0 ? 0 : sum + haversineMeters(stops[i - 1].point, stop.point)),
    0,
  );

  const paths: TripRouteShape['paths'] = [];
  let run: RouteStop[] = [];
  const flush = () => {
    if (run.length >= 2) {
      paths.push({ dayIdx: run[0].dayIdx, points: smoothThrough(run.map(s => s.point)) });
    }
    run = [];
  };
  for (const stop of stops) {
    // One path per day, but each day picks up where the last left off so the
    // silhouette stays a single continuous line.
    if (run.length > 0 && run[run.length - 1].dayIdx !== stop.dayIdx) {
      run.push(stop);
      flush();
      run = [stop];
      continue;
    }
    run.push(stop);
  }
  flush();

  return { paths, stops, distanceM, approximate: true };
}

export function buildRouteShape(
  days: ItineraryDay[],
  routeDays: ItineraryRouteDay[] | null,
): TripRouteShape {
  const stops = collectStops(days);
  const real = routeDays && routeDays.length > 0
    ? shapeFromRouteDays(days, routeDays, stops)
    : null;
  return real ?? shapeFromStops(stops);
}

/** Web Mercator. Keeps the route's shape honest at any latitude. */
function project(point: LatLngPoint): Point {
  const lat = Math.max(-85, Math.min(85, point.lat));
  return {
    x: point.lng,
    y: -((180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 180 / 2))),
  };
}

/**
 * Ramer–Douglas–Peucker in screen space. A week of driving decodes to tens of
 * thousands of points that land on the same pixel; dropping them keeps the
 * preview redraw instant without changing the silhouette.
 */
const RDP_MAX_POINTS = 4000;

function simplify(points: Point[], epsilon: number): Point[] {
  if (points.length <= 2) return points;

  // RDP recurses once per retained point in the worst case; decimate first so a
  // multi-week driving route can't put the recursion depth in the tens of
  // thousands. Every Nth point of a 50k-point road trace is still the same line.
  if (points.length > RDP_MAX_POINTS) {
    const step = Math.ceil(points.length / RDP_MAX_POINTS);
    const decimated = points.filter((_, i) => i % step === 0);
    if (decimated[decimated.length - 1] !== points[points.length - 1]) {
      decimated.push(points[points.length - 1]);
    }
    return simplify(decimated, epsilon);
  }

  const first = points[0];
  const last = points[points.length - 1];
  const dx = last.x - first.x;
  const dy = last.y - first.y;
  const segmentLength = Math.hypot(dx, dy);

  let maxDistance = -1;
  let maxIndex = 0;
  for (let i = 1; i < points.length - 1; i++) {
    const p = points[i];
    const distance = segmentLength === 0
      ? Math.hypot(p.x - first.x, p.y - first.y)
      : Math.abs(dy * p.x - dx * p.y + last.x * first.y - last.y * first.x) / segmentLength;
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }

  if (maxDistance <= epsilon) return [first, last];
  return [
    ...simplify(points.slice(0, maxIndex + 1), epsilon).slice(0, -1),
    ...simplify(points.slice(maxIndex), epsilon),
  ];
}

/**
 * Scale the whole trip — every path and every stop — into `box`, aspect ratio
 * intact and centred. Returns null when there's nothing with extent to draw.
 */
export function fitRouteShape(
  shape: TripRouteShape,
  box: Box,
  options: { simplifyEpsilon?: number } = {},
): FittedRouteShape | null {
  const projectedPaths = shape.paths.map(p => ({ dayIdx: p.dayIdx, points: p.points.map(project) }));
  const projectedStops = shape.stops.map(s => ({ stop: s, point: project(s.point) }));

  const all = [...projectedPaths.flatMap(p => p.points), ...projectedStops.map(s => s.point)];
  if (all.length === 0) return null;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of all) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }

  const spanX = maxX - minX;
  const spanY = maxY - minY;
  if (spanX === 0 && spanY === 0) {
    // A single stop, or every stop on the same spot: park it in the middle.
    const centre = { x: box.x + box.w / 2, y: box.y + box.h / 2 };
    return {
      paths: [],
      stops: projectedStops.map(s => ({ ...s.stop, ...centre })),
    };
  }

  const scale = Math.min(
    spanX === 0 ? Infinity : box.w / spanX,
    spanY === 0 ? Infinity : box.h / spanY,
  );
  const offsetX = box.x + (box.w - spanX * scale) / 2;
  const offsetY = box.y + (box.h - spanY * scale) / 2;
  const toScreen = (p: Point): Point => ({
    x: offsetX + (p.x - minX) * scale,
    y: offsetY + (p.y - minY) * scale,
  });

  const epsilon = options.simplifyEpsilon ?? 1.2;
  return {
    paths: projectedPaths
      .map(p => ({ dayIdx: p.dayIdx, points: simplify(p.points.map(toScreen), epsilon) }))
      .filter(p => p.points.length >= 2),
    stops: projectedStops.map(s => ({ ...s.stop, ...toScreen(s.point) })),
  };
}
